import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Select, MenuItem, FormControl, Typography } from '@mui/material';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import { queryApi } from 'src/api/queryApi';
import { updateQueryNotesField, getDraftCompany } from 'src/utils/queryNotesUtils';
import toast from 'react-hot-toast';

const CompanySelect = ({ query, mutate }) => {
  const { companiesWithActiveExperts } = useExpertManagement();
  const [updating, setUpdating] = useState(false);

  const currentDraftCompany = getDraftCompany(query.notes) || '';

  const [selectedCompany, setSelectedCompany] = useState(currentDraftCompany);

  useEffect(() => {
    setSelectedCompany(currentDraftCompany);
  }, [currentDraftCompany]);

  // Filter for active companies only
  const activeCompanies = Object.values(companiesWithActiveExperts || {})
    .filter((c) => c.hasActiveExperts)
    .map((c) => c.company)
    .sort();

  const handleChange = async (event) => {
    const newCompany = event.target.value;
    setSelectedCompany(newCompany);
    setUpdating(true);

    try {
      const updatedNotes = updateQueryNotesField(query.notes, 'draftCompany', newCompany);

      await queryApi.updateQueryV2({
        queryID: query.id,
        title: query.title,
        description: query.description,
        status: query.status,
        source: query.source,
        topics: query.topics,
        mediaOutlet: query.mediaOutlet,
        dueDate: query.dueDate,
        notes: updatedNotes,
      });

      if (mutate) {
        mutate();
      }
      toast.success('Company updated');
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
      setSelectedCompany(currentDraftCompany);
    } finally {
      setUpdating(false);
    }
  };

  // Prevent navigation when clicking the dropdown
  const handleClick = (e) => {
    e.stopPropagation();
  };

  return (
    <FormControl fullWidth onClick={handleClick} size="small">
      <Select
        value={selectedCompany}
        onChange={handleChange}
        displayEmpty
        disabled={updating}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography color="textSecondary" variant="body2">
                Select Company...
              </Typography>
            );
          }
          return selected;
        }}
      >
        <MenuItem value="" disabled>
          <em>Select Company...</em>
        </MenuItem>
        {activeCompanies.map((company) => (
          <MenuItem key={company} value={company}>
            {company}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

CompanySelect.propTypes = {
  query: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    source: PropTypes.string,
    topics: PropTypes.string,
    notes: PropTypes.string,
    mediaOutlet: PropTypes.string,
    dueDate: PropTypes.string,
  }).isRequired,
  mutate: PropTypes.func,
};

export default CompanySelect;

